import { Globals } from '../../modules/Globals';
import axios from 'axios';
import { StatType } from '../../enums/StatEnums';
import { IStat } from '../../interfaces/IStat';
import { IStats } from '../../interfaces/IStats';
import { IEntityStats } from '../../interfaces/IEntityStats';
import { IItem } from '../../interfaces/IItem';
import { IStatGroup } from '../../interfaces/IStatGroup';
import { IStatsContext } from '../../interfaces/IStatsContext';
import { ICategorySalesResponse } from '../../interfaces/ICategorySalesStats';

function formatDate(d: Date, full = false) {
  let month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear(),
    hour = d.getHours();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  const parts = [year, month, day];

  if (full) {
    const hourStr = hour < 10 ? '0' + hour : String(hour);
    parts.push(hourStr);
  }

  return parts.join('');
}

function toStatDate(date: Date) {
  return formatDate(date, date.getHours() > 0);
}

const apiUrl = Globals.apiPath + '/stats';

export interface IPaymentMethodsStatsOptions {
  includeMethodCounts?: boolean;
  includeMethodOrderTotals?: boolean;
  methodCountMode?: 'used' | 'primary';
}

export interface IProductsSeriesPoint {
  itemId: number;
  date: string;
  hour?: number;
  units: number;
  revenue: number;
}

export interface IProductsSeriesStats {
  interval: string;
  timezone: string;
  entities: Record<
    number,
    {
      id: number;
      name: string | null;
      picture: string | null;
      categoryId: number | null;
      categoryName: string | null;
    }
  >;
  points: IProductsSeriesPoint[];
}

export default class StatsService {
  constructor() {}

  /** Branch business-day parameters (timezone, cutoff, current date). */
  async getContext(restaurantId: number, branchId: number) {
    const res = await axios.get<IStatsContext>(
      `${apiUrl}/context?r=${restaurantId}&b=${branchId}`
    );
    return res.data;
  }

  async getAll(
    restaurantId: number,
    branchId: number,
    type: StatType,
    from: Date,
    to: Date
  ) {
    let res = await axios.get<IStat[]>(
      `${apiUrl}` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&t=${type}` +
        `&from=${from.toISOString()}` +
        `&to=${to.toISOString()}`
    );
    const stats = res.data;

    for (const stat of stats) {
      stat.hour = new Date(stat.hour);
    }

    return stats;
  }

  async getOrdersStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    orderTypeIds?: string[],
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly',
    aggregate?: string,
    includeNonMonetary?: boolean,
    calendar?: boolean
  ) {
    let res = await axios.get<IStats>(
      `${apiUrl}/orders` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (orderTypeIds ? `&types=${orderTypeIds.join(',')}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (aggregate ? `&aggregate=${aggregate}` : '') +
        (includeNonMonetary ? `&includeNonMonetary=1` : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  /**
   * Covers (guest count) stats from closed table sessions. Groups: covers,
   * sittings, sittingsWithCovers, avgPartySize, spendPerCover,
   * avgTurnMinutes, nonMonetaryWithCovers. Revenue-derived values
   * (spendPerCover) are NET of non-monetary settlements (invitación /
   * consumo empleado); the excluded amounts come in nonMonetaryWithCovers.
   */
  async getCoversStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly',
    calendar?: boolean
  ) {
    let res = await axios.get<IStats>(
      `${apiUrl}/covers` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (calendar ? `&calendar=1` : '')
    );
    const stats = res.data;

    return stats;
  }

  static async getPaymentMethodsStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    orderTypeIds?: string[],
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly',
    options?: IPaymentMethodsStatsOptions,
    calendar?: boolean
  ) {
    let res = await axios.get<IStats>(
      `${apiUrl}/paymentMethods` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (orderTypeIds ? `&types=${orderTypeIds.join(',')}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (options?.includeMethodCounts ? `&includeMethodCounts=1` : '') +
        (options?.includeMethodOrderTotals
          ? `&includeMethodOrderTotals=1`
          : '') +
        (options?.methodCountMode
          ? `&methodCountMode=${options.methodCountMode}`
          : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  static async getTipsStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    mode: 'total' | 'payment' = 'total',
    orderTypeIds?: string[],
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly',
    calendar?: boolean
  ) {
    let res = await axios.get<IStats>(
      `${apiUrl}/tips` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        `&mode=${mode}` +
        (orderTypeIds ? `&types=${orderTypeIds.join(',')}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  async getIncomeStats(from: Date, to?: Date) {
    let res = await axios.get<[IStats, IStats]>(
      `${apiUrl}/income` +
        `?from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  async getTotalOrdersStats(from: Date, to?: Date) {
    let res = await axios.get<{ ars: IStats; usd: IStats }>(
      `${apiUrl}/totalOrders` +
        `?from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  async getViewsStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly',
    aggregate?: string,
    calendar?: boolean
  ) {
    let res = await axios.get<IStats>(
      `${apiUrl}/views` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (aggregate ? `&aggregate=${aggregate}` : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  async getProductsStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    calendar?: boolean
  ) {
    let res = await axios.get<IEntityStats<IItem>>(
      `${apiUrl}/products` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }

  async getProductsSeries(params: {
    restaurantId: number;
    branchId: number;
    from: Date;
    to?: Date;
    itemIds: number[];
    interval?: 'auto' | 'hourly' | 'daily' | 'weekly' | 'monthly';
    orderTypeIds?: string[];
    calendar?: boolean;
  }): Promise<IProductsSeriesStats> {
    const {
      restaurantId,
      branchId,
      from,
      to,
      itemIds,
      interval,
      orderTypeIds,
      calendar,
    } = params;

    if (!Array.isArray(itemIds) || itemIds.length === 0) {
      throw new Error('itemIds is required');
    }

    let res = await axios.get<IProductsSeriesStats>(
      `${apiUrl}/productsSeries` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        `&itemIds=${itemIds.join(',')}` +
        (orderTypeIds ? `&types=${orderTypeIds.join(',')}` : '') +
        (interval ? `&interval=${interval}` : '') +
        (calendar ? `&calendar=1` : '')
    );

    return res.data;
  }

  /** Category sales rollup (reporting tree or flat menu categories). */
  async getCategorySales(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    dimension: 'reporting' | 'menu' = 'reporting',
    calendar?: boolean
  ): Promise<ICategorySalesResponse> {
    const res = await axios.get<ICategorySalesResponse>(
      `${apiUrl}/categories` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        `&dimension=${dimension}` +
        (calendar ? `&calendar=1` : '')
    );
    return res.data;
  }

  /** Club top-ups over time: principal + gifted bonus series. */
  async getClubTopupsStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    mode: 'total' | 'payment' = 'total',
    calendar?: boolean
  ): Promise<IStats> {
    const res = await axios.get<IStats>(
      `${apiUrl}/club-topups` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        `&mode=${mode}` +
        (calendar ? `&calendar=1` : '')
    );
    return res.data;
  }

  async getStockConsumptionStats(
    restaurantId: number,
    branchId: number,
    from: Date,
    to?: Date,
    movementTypes?: number[],
    calendar?: boolean
  ) {
    let res = await axios.get<IEntityStats<any>>(
      `${apiUrl}/stock-consumption` +
        `?r=${restaurantId}` +
        `&b=${branchId}` +
        `&from=${toStatDate(from)}` +
        (to ? `&to=${toStatDate(to)}` : '') +
        (movementTypes ? `&types=${movementTypes.join(',')}` : '') +
        (calendar ? `&calendar=1` : ''),
      {
        /*headers: {
          'Cache-Control': 'no-cache'
        }*/
      }
    );
    const stats = res.data;

    return stats;
  }
}
